import { Close, LayersOutlined } from '@mui/icons-material';
import { LayersButton, MenuHeader, MenuLoading } from './styles';
import { IconButton } from '@/components/atoms/Button';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Paper,
} from '@mui/material';
import { Text } from '@/components/atoms/text';
import { useTheme } from '@mui/material/styles';
import { Fragment, useState, ChangeEvent, useEffect, ReactNode } from 'react';
import { useApolloClient } from '@apollo/client';
import { QUERY_DIMENSIONS } from '@/graphql/queries/dimensions';
import { DynamicIcon } from '@/components/atoms/dynamicIcon';
import { useLanguage } from '@/hooks/useLanguage';
import { Desktop } from '@/components/molecules/viewport/desktop';
import { Mobile } from '@/components/molecules/viewport/mobile';
import { BottomSheet } from '@/components/atoms/bottomsheet';
import { useTranslation } from '@/hooks/useTranslation';
import { useAdvancedFiltersUrl } from '@/hooks/useAdvancedFiltersUrl';
import { AppRouters } from '@/utils/constants/routers';
import { applyDimensionLayerToFilters } from '@/utils/functions/advancedFilters';

interface Dimension {
  id: number;
  name: string;
  icon: string;
  bigquery_id: string;
}

export type Layer = {
  label: string;
  value: number;
  icon: ReactNode;
  bigquery_id: string;
};

interface LayersProps {
  selectedLayer: number;
  setOpenLayers?: (open: boolean) => void;
  setSelectedLayer: (layer: number) => void;
  setSelectedBigqueryId: (bigqueryId: string) => void;
  setSelectedDimensionName?: (name: string) => void;
  noBorderTop?: boolean;
  isOpen?: boolean;
}

export const Layers = ({
  selectedLayer,
  setOpenLayers,
  setSelectedLayer,
  setSelectedBigqueryId,
  setSelectedDimensionName,
  noBorderTop,
  isOpen: isOpenProp,
}: LayersProps) => {
  const theme = useTheme();
  const apolloClient = useApolloClient();

  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { setFiltersToUrl, getFiltersFromUrl } = useAdvancedFiltersUrl();

  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [dimensionsLoading, setDimensionsLoading] = useState(false);

  const [openLayersInternal, setOpenLayersInternal] = useState(false);

  const openLayers = isOpenProp !== undefined ? isOpenProp : openLayersInternal;

  useEffect(() => {
    if (isOpenProp !== undefined) {
      setOpenLayersInternal(isOpenProp);
    }
  }, [isOpenProp]);

  const handleOpenLayers = () => {
    setOpenLayersInternal(true);
    setOpenLayers?.(true);
  };

  const handleCloseLayers = () => {
    setOpenLayersInternal(false);
    setOpenLayers?.(false);
  };

  const loadingLayers = Array.from({ length: 7 }, (_, index) => index);

  const layers: Layer[] = [
    {
      label: `${t('page.municipality.municipality.card.scores.footer')}`,
      value: -10,
      icon: <DynamicIcon name={'BarChart'} color={theme.palette.action.active} />,
      bigquery_id: 'overall',
    },
    ...dimensions.map((dimension) => ({
      label: dimension.name,
      value: dimension.id,
      icon: <DynamicIcon name={dimension.icon} color={theme.palette.action.active} />,
      bigquery_id: dimension.bigquery_id,
    })),
  ];

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const layer = layers.find((l) => l.value === Number(event.target.value));

    if (layer?.value === -10) {
      setSelectedBigqueryId('');
      setSelectedLayer(-10);
      setSelectedDimensionName?.('');
      const fresh = getFiltersFromUrl();
      const updated = applyDimensionLayerToFilters(fresh, -10);
      setFiltersToUrl(AppRouters.home, updated);
      return;
    }

    if (layer) {
      setSelectedLayer(layer.value);
      setSelectedBigqueryId(layer.bigquery_id);
      setSelectedDimensionName?.(layer.label);
    }
  };

  useEffect(() => {
    const layer = layers.find((l) => l.value === selectedLayer);
    if (layer) {
      setSelectedBigqueryId(layer.bigquery_id);
      setSelectedDimensionName?.(layer.label);
    }
  }, [selectedLayer, layers, setSelectedBigqueryId, setSelectedDimensionName]);

  useEffect(() => {
    const fetchDimensions = async () => {
      try {
        setDimensionsLoading(true);
        const { data } = await apolloClient.query<{
          dimensions: { data: Dimension[] };
        }>({
          query: QUERY_DIMENSIONS,
          variables: {
            pagination: { page: 1, limit: 10 },
            language: currentLanguage,
          },
        });
        setDimensions(data.dimensions.data);
      } catch (error) {
        // Error handled silently - dimensions will remain empty
      } finally {
        setDimensionsLoading(false);
      }
    };
    fetchDimensions();
  }, [currentLanguage]);

  const layersContent = (
    <>
      {dimensionsLoading ? (
        <MenuLoading>
          {loadingLayers.map((index) => (
            <Skeleton
              key={index}
              width="100%"
              height={40}
              animation="wave"
              sx={{ transform: 'none' }}
            />
          ))}
        </MenuLoading>
      ) : (
        <FormControl sx={{ width: '100%' }}>
          <RadioGroup name="layers" value={selectedLayer} onChange={handleChange}>
            {layers.map(({ label, value, icon }, index) => (
              <Fragment key={index}>
                <FormControlLabel
                  value={value}
                  control={<Radio color="primary" sx={{ padding: theme.spacing(1) }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                      <Box sx={{ marginRight: theme.spacing(1) }}>{icon}</Box>
                      <Text variant="subtitle2" color="text.primary">
                        {label}
                      </Text>
                    </Box>
                  }
                  labelPlacement="start"
                  sx={{ margin: 0, justifyContent: 'space-between' }}
                />
                {index !== layers.length - 1 && <Divider variant="fullWidth" />}
              </Fragment>
            ))}
          </RadioGroup>
        </FormControl>
      )}
    </>
  );

  return (
    <>
      <LayersButton variant="outlined" onClick={handleOpenLayers} noBorderTop={noBorderTop}>
        <LayersOutlined color="action" />
      </LayersButton>

      <Desktop>
        {openLayers && (
          <Paper
            elevation={3}
            sx={{
              position: 'fixed',
              right: theme.spacing(8),
              top: theme.spacing(12),
              width: '320px',
              padding: theme.spacing(2),
              zIndex: 1000,
            }}
          >
            <MenuHeader>
              <Text variant="subtitle1" color="text.primary">
                {t('label.label.active_layer')}
              </Text>

              <IconButton onClick={handleCloseLayers}>
                <Close color="action" />
              </IconButton>
            </MenuHeader>

            {layersContent}
          </Paper>
        )}
      </Desktop>

      <Mobile>
        <BottomSheet
          isOpen={openLayers}
          onClose={handleCloseLayers}
          snapPoints={[1, 0.5, 0]}
          initialSnap={1}
        >
          <Box sx={{ padding: theme.spacing(2, 3) }}>
            <Text variant="subtitle1" color="text.primary" sx={{ marginBottom: theme.spacing(2) }}>
              {t('label.label.active_layer')}
            </Text>

            {layersContent}
          </Box>
        </BottomSheet>
      </Mobile>
    </>
  );
};
